import { Box, Stack } from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  QuotedMessageFragment,
} from "gql/graphql";
import Link from "modules/layouts/_shared/link";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import moment from "moment";
import { useRouter } from "next/router";
import React, { memo, useCallback } from "react";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import QuotedMessage from "./quoted-message";
import {
  MESSAGE_ID_QUERY_STRING,
  MESSAGE_THREAD_ID_QUERY_STRING,
  TIMESTAMP_QUERY_STRING,
} from "modules/hooks/use-query-string";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
}

const QuotedMessages = ({ messageThread, message }: IProps) => {
  const { pathname, query } = useRouter();

  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const quotedMessageList = message.quotedMessageList;

  const quotedMessageHref = useCallback(
    (quotedMessage: QuotedMessageFragment) => ({
      pathname,
      query: {
        ...query,
        [MESSAGE_THREAD_ID_QUERY_STRING]: quotedMessage.messageThreadId,
        [MESSAGE_ID_QUERY_STRING]: quotedMessage.messageId,
        // This gives other components a way to rerun the autoscroll logic even if the IDs in the url don't change (e.g. clicking on a search result a second time or "Jump To Original Message" a second time.)
        [TIMESTAMP_QUERY_STRING]: moment().unix(),
      },
    }),
    [pathname, query],
  );

  if (quotedMessageList.length === 0) {
    return null;
  }

  return (
    <Stack mb={0.5} spacing={4}>
      {quotedMessageList.map((quotedMessage, index) => {
        const maxLines =
          quotedMessage.messageThreadId === messageThread.id ? 2 : undefined;
        return (
          <Box key={index} pl={isViewingTranslatedMessage ? 3 : 0}>
            {quotedMessage.canAccessMessageThread ? (
              <SyncoTooltip
                placement="top-start"
                label="Jump to original message"
                openDelay={500}
                hasArrow={false}
                isDisabled={!quotedMessage.canAccessMessageThread}
              >
                <Link href={quotedMessageHref(quotedMessage)}>
                  <QuotedMessage
                    messageThread={messageThread}
                    quotedMessage={quotedMessage}
                    maxLines={maxLines}
                  />
                </Link>
              </SyncoTooltip>
            ) : (
              <QuotedMessage
                messageThread={messageThread}
                quotedMessage={quotedMessage}
                maxLines={maxLines}
              />
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default memo(QuotedMessages);
